import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress
} from '@mui/material';
import { Warning as WarningIcon, Timer as TimerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SessionTimeoutWarning = ({ sessionExpiresAt, hasRefreshToken, onLogout }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const WARNING_THRESHOLD = 7 * 60 * 1000; // Cảnh báo trước 7 phút

  useEffect(() => {
    // Nếu người dùng chọn ghi nhớ (có refresh token) -> Không hiện cảnh báo và không tự logout
    if (!sessionExpiresAt || hasRefreshToken) return;

    const checkTimeout = () => {
      const now = new Date().getTime();
      const expiresAt = new Date(sessionExpiresAt).getTime();
      const diff = expiresAt - now;

      // Debug log (optional: reduce frequency or remove later)
      // console.log(`[SessionCheck] Exp: ${new Date(expiresAt).toLocaleTimeString()}, Now: ${new Date(now).toLocaleTimeString()}, Diff: ${Math.floor(diff / 1000)}s`);

      if (diff <= 0) {
        onLogout();
        navigate('/login');
        return;
      }

      // Show dialog if within threshold and NOT manually dismissed
      if (diff <= WARNING_THRESHOLD && !open && !isDismissed) {
        setOpen(true);
      }

      // Reset isDismissed if session is somehow extended (though here it's static)
      if (diff > WARNING_THRESHOLD) {
        setOpen(false);
        setIsDismissed(false);
      }

      setTimeLeft(diff);
    };

    // Check immediately
    checkTimeout();

    // Set interval to check every second
    const interval = setInterval(checkTimeout, 1000);

    return () => clearInterval(interval);
  }, [sessionExpiresAt, WARNING_THRESHOLD, open, onLogout, navigate]);

  const formatTimeLeft = (ms) => {
    if (ms <= 0) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = timeLeft ? Math.max(0, Math.min(100, (timeLeft / WARNING_THRESHOLD) * 100)) : 100;

  return (
    <Dialog
      open={open}
      aria-labelledby="session-timeout-dialog-title"
      aria-describedby="session-timeout-dialog-description"
      PaperProps={{
        sx: {
          bgcolor: '#1e293b',
          color: '#fff',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          minWidth: 320
        }
      }}
    >
      <DialogTitle id="session-timeout-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon sx={{ color: '#f59e0b' }} />
        Phiên đăng nhập sắp hết hạn
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="session-timeout-dialog-description" sx={{ color: '#94a3b8', mb: 2 }}>
          Phiên làm việc của bạn sẽ kết thúc sau để đảm bảo an toàn. Vui lòng hoàn tất công việc hoặc đăng nhập lại.
        </DialogContentText>

        <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', p: 2, borderRadius: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <TimerIcon sx={{ color: '#00D9FF' }} />
            <Typography variant="h4" sx={{ color: '#00D9FF', fontWeight: 700, fontFamily: 'monospace' }}>
              {formatTimeLeft(timeLeft)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: progress < 30 ? '#ef4444' : '#00D9FF'
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={() => {
            setOpen(false);
            setIsDismissed(true);
          }}
          sx={{ color: '#94a3b8', textTransform: 'none' }}
        >
          Để sau
        </Button>
        <Button
          variant="contained"
          onClick={onLogout}
          sx={{
            bgcolor: '#ef4444',
            '&:hover': { bgcolor: '#dc2626' },
            textTransform: 'none',
            px: 3
          }}
        >
          Đăng xuất ngay
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;
